import React, { useEffect, useMemo } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";
import CampaignCard from "../../components/common/CampaignCard/CampaignCard";
import CampaignCardSkeleton from "../../components/common/CampaignCard/CampaignCardSkeleton";
import { SwipeRow } from "../Temples/shared";
import youtubeIcon from "../../assets/Images/TempleDetail/youtube.svg";
import playMobile from "../../assets/Images/TempleDetail/mobile/play.svg";
import headingLines from "../../assets/Images/TempleDetail/heading-lines.svg";
import headingLotus from "../../assets/Images/TempleDetail/heading-lotus.svg";
import { T, FONT, HINDI_FONT } from "./shared";

// YouTube video id from watch / youtu.be / shorts / embed links
const youtubeId = (url) => {
  const m = String(url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|live\/))([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

// Figma 3488:15537 — the "How To Donate" video in the temple gold header.
// Temple pages use their own video (admin → Temples → Page Content → "How To Donate video",
// cms page "temples" / section "how_to_donate") and fall back to the site-wide CMS
// "How To Donate" video when that has no link.
export const TempleHowToDonate = () => {
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const cms = useMemo(() => {
    const rows = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const temple = rows.find((s) => s?.page === "temples" && s?.section === "how_to_donate" && Number(s?.status ?? 1) === 1);
    if (temple?.link && String(temple.link).trim()) {
      const id = youtubeId(temple.link);
      return {
        section_title: temple.section_title,
        link: String(temple.link).trim(),
        // own thumbnail when uploaded, otherwise the video's YouTube thumbnail
        image: temple.image || (id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null),
      };
    }
    return rows.find((s) => s?.page === "How To Donate" && s?.section === "How To Donate") || null;
  }, [cmsHome]);
  if (!cms?.image && !cms?.link) return null;
  const title = (cms.section_title || "How To Donate").trim();
  // Phones follow Figma "Mobile revamp" 739:17359

  return (
    <Box sx={{ background: "#FFFFFF", border: "1px solid #F1F1F1", borderRadius: { xs: "12px", sm: "10px" }, p: { xs: "12px", sm: "20px" }, display: "flex", flexDirection: "column", gap: { xs: "12px", sm: "14px" } }}>
      <Box sx={{ background: "rgba(201,149,43,0.16)", borderRadius: "5px", pl: { xs: "10px", sm: "16px" }, pr: "10px", py: "10px" }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: "13px", sm: "14px" }, lineHeight: { xs: 1.5, sm: "21px" }, color: T.gold }}>{title.endsWith(":") ? title : `${title}:`}</Typography>
      </Box>
      {cms.image && (
        <Box
          component={cms.link ? "a" : "div"}
          href={cms.link || undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Watch how to donate"
          // phones: YouTube frame (16:9); larger screens keep the 176px card
          sx={{ position: "relative", display: "block", height: { xs: "auto", sm: "176px" }, aspectRatio: { xs: "16 / 9", sm: "auto" }, borderRadius: "10px", overflow: "hidden", "&:hover img.thumb": { transform: "scale(1.03)" } }}
        >
          <Box className="thumb" component="img" src={cms.image} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s" }} />
          <Box sx={{ position: "absolute", inset: 0, background: "rgba(20,20,20,0.35)", display: { xs: "none", sm: "block" } }} />
          <Box component="img" src={youtubeIcon} alt="" sx={{ position: "absolute", left: "50%", top: "50%", width: "30px", height: "30px", transform: "translate(-50%, -50%)", display: { xs: "none", sm: "block" } }} />
          <Box component="img" src={playMobile} alt="" sx={{ position: "absolute", left: "50%", top: "50%", width: "40px", height: "28px", transform: "translate(-50%, -50%)", display: { xs: "block", sm: "none" } }} />
        </Box>
      )}
    </Box>
  );
};

// Figma 3408:14447 — other live temple campaigns, using the site's campaign cards.
export const OtherTemples = ({ temples, loading }) => {
  const isMobile = useMediaQuery("(max-width:599px)");
  if (!loading && !temples.length) return null;
  const cards = loading
    ? Array.from({ length: 3 }, (_, i) => <CampaignCardSkeleton key={i} isCampaign />)
    : temples.map((t) => <CampaignCard key={t.id} campaignData={t} isCampaign />);

  return (
    <Box component="section" sx={{ background: "#FFFFFF", px: isMobile ? 0 : "40px", py: isMobile ? "16px" : "80px", display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? "24px" : "36px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", textAlign: "center", px: "20px" }}>
        <Typography sx={{ fontFamily: HINDI_FONT, fontSize: isMobile ? "20px" : "24px", lineHeight: isMobile ? "normal" : "22px", color: T.gold }}>॥ सेवा ॥</Typography>
        <Typography component="h2" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "25px" : "32px", lineHeight: isMobile ? 1.5 : "40px", color: T.ink, mt: isMobile ? 0 : "6px" }}>
          Other Temples Waiting For You
        </Typography>
        <Box sx={{ position: "relative", width: "120px", height: "14px", display: isMobile ? "none" : "block" }}>
          <Box component="img" src={headingLines} alt="" sx={{ position: "absolute", left: 0, top: "1.4px", width: "120px", height: "11.1px" }} />
          <Box component="img" src={headingLotus} alt="" sx={{ position: "absolute", left: "46.3px", top: "5.3px", width: "27.3px", height: "3.3px" }} />
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: isMobile ? "13px" : "15px", lineHeight: isMobile ? 1.5 : "24px", color: "#6B6560" }}>More sacred spaces that need your support.</Typography>
      </Box>
      {isMobile ? (
        <Box sx={{ width: "100%" }}>
          <SwipeRow itemWidth={300} px={16}>{cards}</SwipeRow>
        </Box>
      ) : (
        <Box sx={{ width: "100%", maxWidth: 1299, display: "grid", gridTemplateColumns: "repeat(auto-fill, 417px)", justifyContent: "center", columnGap: "24px", rowGap: "40px" }}>
          {cards}
        </Box>
      )}
    </Box>
  );
};
